import type { OverdueSection } from "#/types/report";
import { formatCurrency } from "#/utils/formatCurrency";
import { Badge, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { exportOverdueItemsCsv } from "../utils/exportOverdueItemsCsv";
import CollapsibleItemTable from "./CollapsibleItemTable";
import ProductOverdueSummary from "./ProductOverdueSummary";
import ReportSection from "./ReportSection";
import SiteOverdueSummary from "./SiteOverdueSummary";

interface NeverScannedSectionProps {
  data: OverdueSection;
  tooltip?: ReactNode;
}

const NeverScannedSection = ({ data, tooltip }: NeverScannedSectionProps) => {
  const { t } = useTranslation();

  const handleExportCsv = useCallback(() => {
    exportOverdueItemsCsv({
      items: data.items,
      filenamePrefix: "never-scanned",
      includeScanAge: false,
      t,
    });
  }, [data.items, t]);

  if (data.totalCount === 0) {
    return (
      <ReportSection title={t("reports.inventoryScan.neverScanned.title")} tooltip={tooltip}>
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {t("reports.inventoryScan.neverScanned.allScanned")}
        </div>
      </ReportSection>
    );
  }

  return (
    <ReportSection
      title={t("reports.inventoryScan.neverScanned.title")}
      tooltip={tooltip}
      rightSection={
        <div className="flex items-center gap-2">
          <Badge variant="light" color="yellow" size="sm">
            {t("reports.inventoryScan.neverScanned.itemCount", { count: data.totalCount })}
          </Badge>
          <Badge variant="light" color="orange" size="sm">
            {formatCurrency(data.totalEstimatedValue)}
          </Badge>
          <Button
            data-export-csv-button
            leftSection={<IconDownload size={14} />}
            variant="outline"
            size="xs"
            onClick={handleExportCsv}
          >
            {t("common.exportCsv")}
          </Button>
        </div>
      }
    >
      {(data.byProduct.length > 0 || data.bySite.length > 0) && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {data.byProduct.length > 0 && <ProductOverdueSummary products={data.byProduct} />}
          {data.bySite.length > 0 && <SiteOverdueSummary sites={data.bySite} />}
        </div>
      )}
      <CollapsibleItemTable items={data.items} />
    </ReportSection>
  );
};

export default NeverScannedSection;
